import React, { useEffect, useMemo, useState } from 'react';
import { Button, Message, Placeholder, SelectPicker } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';
import {
  useSharePointDrives,
  useSharePointSiteContext,
} from '../../hooks/useQueries';

export default function SharePointLocationPicker({
  value,
  onChange,
  disabled = false,
  saving = false,
  size = 'md',
}) {
  // Opened from inside another dialog, this one stacks above it and dims it on its own.
  const [open, setDialogOpen] = useState(false);
  const [selectedDriveId, setSelectedDriveId] = useState(value?.sharepoint_drive_id || value?.document_library_id || null);

  // Only the dialog needs the site: it is what the drive list hangs off. The
  // form no longer prints it, so nothing fetches it until somebody opens this.
  const contextQuery = useSharePointSiteContext({ enabled: open });
  const resolvedSiteId = contextQuery.data?.site?.id || null;

  const drivesQuery = useSharePointDrives(resolvedSiteId, {
    enabled: open && Boolean(resolvedSiteId),
  });

  const driveOptions = useMemo(
    () =>
      (drivesQuery.data || []).map((drive) => ({
        label: drive.name,
        value: drive.id,
      })),
    [drivesQuery.data]
  );

  const linkedDriveId = value?.sharepoint_drive_id || value?.document_library_id || null;

  useEffect(() => {
    if (!open) return;
    setSelectedDriveId(value?.sharepoint_drive_id || value?.document_library_id || null);
  }, [open, value?.sharepoint_drive_id, value?.document_library_id]);

  const applySelection = () => {
    onChange?.({
      sharepoint_drive_id: selectedDriveId || null,
      document_library_id: selectedDriveId || null,
    });
    setDialogOpen(false);
  };

  // Unlinking rather than swapping in place: the dialog picks a library, and
  // the way to a different one is to clear this and pick again.
  const clearSelection = () => {
    setSelectedDriveId(null);
    onChange?.({ sharepoint_drive_id: null, document_library_id: null });
  };

  /*
    The control is the button, and the button is the state.

    It used to print the site's URL and the words "Document Library" above
    whatever was holding it — under a heading already saying Document Library —
    which read as the same thing said three times, one of them a URL nobody
    needs. Linked or not linked is the only fact here, and the button says it.
  */
  return (
    <div>
      <div className="d-flex gap-2 flex-wrap align-items-center">
        {linkedDriveId ? (
          <>
            <Button appearance="ghost" color="green" disabled>
              <i className="bi bi-check-lg me-1" />
              Document Library linked
            </Button>
            <Button appearance="link" size="sm" disabled={disabled || saving} onClick={clearSelection}>
              Clear
            </Button>
          </>
        ) : (
          <Button appearance="ghost" size="sm" disabled={disabled || saving} onClick={() => setDialogOpen(true)}>
            Link Document Library
          </Button>
        )}
      </div>

      <Modal open={open} onClose={() => setDialogOpen(false)} size={size}>
        <Modal.Header>
          <Modal.Title>Select Document Library</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {contextQuery.isLoading ? <Placeholder.Paragraph rows={3} active /> : null}

          {contextQuery.error ? (
            <Message type="error" showIcon header="SharePoint site configuration error" style={{ marginBottom: 12 }}>
              {String(contextQuery.error?.message || contextQuery.error)}
            </Message>
          ) : null}

          {!contextQuery.error ? (
            <SelectPicker
              data={driveOptions}
              value={selectedDriveId}
              onChange={(next) => setSelectedDriveId(next || null)}
              placeholder="Select a library"
              style={{ width: '100%' }}
              cleanable={false}
              loading={drivesQuery.isLoading}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setDialogOpen(false)}>Cancel</NsiButton>
          <NsiButton
            variant="primary"
            onClick={applySelection}
            disabled={!resolvedSiteId || !selectedDriveId}
          >
            Use This Location
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
